import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, apiFetch } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// User role constants
export const UserRoles = {
  ADMIN: 'admin',
  INHABER: 'inhaber',
  MITARBEITER: 'mitarbeiter'
};

// Einmalige Aufräum-Routine: alte localStorage-Reste aus Phase-1 entfernen.
// (Company-JWT lag früher in localStorage.token — jetzt ausschließlich im
// httpOnly-Cookie `sv_session`. Der Mitarbeiter-Token bleibt vorerst
// separat, siehe Kommentar in loginMitarbeiter.)
const purgeLegacyCompanyToken = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('company');
  } catch {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null); // 'company' or 'mitarbeiter'
  const [role, setRole] = useState(null); // 'admin', 'inhaber', 'mitarbeiter'

  useEffect(() => {
    // Legacy-Cleanup: alte Company-JWT-Reste aus localStorage entfernen.
    purgeLegacyCompanyToken();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Prime CSRF-Token für alle Write-Requests (idempotent, kein Fehler wenn fail)
    try {
      const { ensureCsrfToken } = await import('../services/api');
      await ensureCsrfToken();
    } catch {
      // ignore — Write-Requests holen den Token dann on-demand
    }

    // Company-Auth läuft jetzt ausschließlich über das httpOnly-Cookie `sv_session`.
    // Wir versuchen /auth/me — Erfolg = eingeloggt, 401 = anonym.
    try {
      const response = await authAPI.getMe();
      const data = response.data;
      if (data?.company) {
        setCompany(data.company);
        setUserType('company');
        setRole(data.role || data.company?.role || UserRoles.INHABER);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }
    } catch {
      // Kein gültiges Cookie → weiter zum Mitarbeiter-Check
    }

    // Mitarbeiter-Auth ist ein separater Flow (Token in localStorage.mitarbeiterToken).
    // Wird in einer späteren Iteration ebenfalls auf Cookie umgestellt (eigener
    // Cookie-Name `sv_emp_session`, damit Chef & Mitarbeiter im gleichen Browser
    // nicht kollidieren).
    const mitarbeiterToken = localStorage.getItem('mitarbeiterToken');
    if (mitarbeiterToken) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mitarbeiter/me?token=${mitarbeiterToken}`);
        const data = await response.json();
        if (data.success) {
          setEmployee(data.employee);
          setUserType('mitarbeiter');
          setRole(UserRoles.MITARBEITER);
          setIsAuthenticated(true);
        } else {
          logoutMitarbeiter();
        }
      } catch (error) {
        console.error('Mitarbeiter auth check failed:', error);
        logoutMitarbeiter();
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { company, role: userRole } = response.data;
      setCompany(company);
      setUserType('company');
      setRole(userRole || company?.role || UserRoles.INHABER);
      setIsAuthenticated(true);
      return { success: true, role: userRole || company?.role };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login fehlgeschlagen'
      };
    }
  };

  const loginMitarbeiter = async (email, password) => {
    try {
      const response = await apiFetch(`/api/mitarbeiter/login`, {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();

      if (data.success) {
        localStorage.setItem('mitarbeiterToken', data.token);
        localStorage.setItem('employee', JSON.stringify(data.employee));
        setEmployee(data.employee);
        setUserType('mitarbeiter');
        setRole(UserRoles.MITARBEITER);
        setIsAuthenticated(true);
        return { success: true, role: UserRoles.MITARBEITER };
      } else {
        return { success: false, error: data.detail || 'Login fehlgeschlagen' };
      }
    } catch (error) {
      return {
        success: false,
        error: 'Netzwerkfehler - Login fehlgeschlagen'
      };
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);
      const { company, role: userRole } = response.data;
      setCompany(company);
      setUserType('company');
      setRole(userRole || company?.role || UserRoles.INHABER);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Registrierung fehlgeschlagen');
      const action = typeof detail === 'object' ? detail?.action : null;
      return {
        success: false,
        error: message,
        action: action,
        existingEmail: typeof detail === 'object' ? detail?.email : null };
    }
  };

  /**
   * Smart-Register für den Checkout-Flow: legt bei neuer E-Mail eine Firma an,
   * oder loggt einen bestehenden Account ein, wenn das Passwort korrekt ist.
   */
  const registerOrLogin = async (data) => {
    try {
      const response = await apiFetch(`/api/auth/register-or-login`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      const body = await response.json();
      if (!response.ok) {
        const d = body.detail;
        const message = typeof d === 'string' ? d : (d?.message || 'Anmeldung fehlgeschlagen');
        const action = typeof d === 'object' ? d?.action : null;
        return {
          success: false,
          error: message,
          action,
          existingEmail: typeof d === 'object' ? d?.email : null };
      }
      const { company, role: userRole, existing } = body;
      setCompany(company);
      setUserType('company');
      setRole(userRole || company?.role || UserRoles.INHABER);
      setIsAuthenticated(true);
      return { success: true, existing: !!existing };
    } catch (error) {
      return { success: false, error: 'Netzwerkfehler - bitte erneut versuchen' };
    }
  };

  const logout = async () => {
    // Backend-Logout löscht httpOnly-Cookie + CSRF-Cookie
    try { await authAPI.logout(); } catch { /* Cookies waren evtl. schon abgelaufen — trotzdem clientseitig aufräumen */ }
    // Legacy-Cleanup (falls Reste)
    purgeLegacyCompanyToken();
    localStorage.removeItem('mitarbeiterToken');
    localStorage.removeItem('employee');
    setCompany(null);
    setEmployee(null);
    setUserType(null);
    setRole(null);
    setIsAuthenticated(false);
  };

  const logoutMitarbeiter = () => {
    localStorage.removeItem('mitarbeiterToken');
    localStorage.removeItem('employee');
    setEmployee(null);
    if (userType === 'mitarbeiter') {
      setUserType(null);
      setRole(null);
      setIsAuthenticated(false);
    }
  };

  // Helper functions
  const isAdmin = () => role === UserRoles.ADMIN;
  const isInhaber = () => role === UserRoles.INHABER;
  const isMitarbeiter = () => role === UserRoles.MITARBEITER;
  const getUser = () => userType === 'mitarbeiter' ? employee : company;

  const value = {
    company,
    employee,
    isAuthenticated,
    loading,
    userType,
    role,
    login,
    loginMitarbeiter,
    register,
    registerOrLogin,
    logout,
    logoutMitarbeiter,
    isAdmin,
    isInhaber,
    isMitarbeiter,
    getUser,
    UserRoles
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
